import { ORDER_PAYMENT_STATES, ORDER_PAYMENT_STATUSES, SCREENS } from '../../../services/exports/Constants';
import { FC, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import LoadingPage from '../../../components-library/loading/LoadingPage';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../components-library/headers/PageHeader';
import { useGetGiftCardOrderQuery, useGetOrderPaymentStatusQuery } from '../../../store/api-slices';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Download, Mail } from 'lucide-react';
import CustomButton from '../../../components-library/buttons/CustomButton';
import { ButtonTypes, GiftCardOrder } from '../../../services/exports/Interfaces';
import useRouter from '../../../hooks/navigation/useRouter';
import useUrlParams from '../../../hooks/utility/useUrlParams';
import { RedirectStatus } from '../../OrderDetailsScreen';
import useOffsiteLateSuccess from '../../../hooks/payment/useOffsiteLateSuccess';
import { useDispatch, useSelector } from 'react-redux';
import giftCardsBasketSlice from '../../../store/slices/GiftCardBasketSlice';
import { selectCompany } from '../../../store/slices/InitialDataSlice';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import { saveAs } from 'file-saver';
import { Routes } from '../../../modules/mobile/exports/Enums';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import useScreenType from '../../../hooks/utility/useScreenType';

const POLLING_INTERVAL = 4000;

export interface Props {
  uuid?: string;
  redirect_status?: RedirectStatus;
  /** `1` on the return from an offsite payment: see useOffsiteLateSuccess. */
  offsite?: string;
  onChangePaymentStatus?: (status: ORDER_PAYMENT_STATUSES) => void;
}

const GiftCardsOrderDetailsScreen: FC<Props> = (mobileProps) => {
  const params = useParams();
  const search = useUrlParams();
  const webProps: Props = {
    ...params,
    ...search,
  };
  const props: Props = Object.keys(mobileProps).length ? mobileProps : webProps;
  const dispatch = useDispatch();
  // State, not a ref: RTK Query re-reads `skip` on render, and a repeated poll that returns
  // identical data does not re-render on its own — a ref flipped mid-poll could never un-skip the
  // order query.
  const [shouldPoll, setShouldPoll] = useState(!!props.uuid);
  const { data } = useGetOrderPaymentStatusQuery(props.uuid, {
    pollingInterval: POLLING_INTERVAL,
    skip: !shouldPoll,
  });
  const { data: order } = useGetGiftCardOrderQuery(props.uuid, {
    skip: shouldPoll,
  });
  const { navigate } = useRouter();
  const { toggleInfoModal } = useAppMethods();
  const { isMobileApp } = useMobileApp();
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });
  const paymentFailureHandledRef = useRef(false);
  const notePaymentStatus = useOffsiteLateSuccess(props, () => dispatch(giftCardsBasketSlice.actions.reset(undefined)));

  const orderFailedHandler = useCallback(() => {
    if (paymentFailureHandledRef.current) {
      return;
    }

    paymentFailureHandledRef.current = true;

    toggleInfoModal({
      openModal: true,
      title: t('error_messages.payment_failed'),
      primaryButtonTitle: t('buttons.back_to_checkout'),
      onConfirm: () =>
        navigate({
          web: {
            to: SCREENS.GIFT_CARDS_CHECKOUT,
          },
          mobile: {
            path: Routes.GiftCardCheckout,
          },
        }),
    });
  }, [toggleInfoModal, navigate, t]);

  useEffect(() => {
    const paymentState = data?.payment_state;

    if (!paymentState && !data?.payment_status) {
      return;
    }

    notePaymentStatus(data?.payment_status);

    // payment_state is the derived discriminator: a failed or abandoned gift-card payment keeps
    // payment_status at 'pending' forever, so polling on the status alone never stops and the
    // "processing payment" loader never resolves. Any terminal state means the backend has decided
    // — stop polling so the order query un-skips and loads.
    if (paymentState && paymentState !== ORDER_PAYMENT_STATES.AWAITING_PAYMENT) {
      setShouldPoll(false);

      if (paymentState === ORDER_PAYMENT_STATES.FAILED) {
        orderFailedHandler();
      }

      return;
    }

    setShouldPoll(data?.payment_status === ORDER_PAYMENT_STATUSES.PENDING);
  }, [data?.payment_state, data?.payment_status, orderFailedHandler]);

  useEffect(() => {
    if (props.redirect_status === RedirectStatus.failed) {
      orderFailedHandler();

      return;
    }

    if (props.redirect_status !== RedirectStatus.succeeded) {
      return;
    }

    dispatch(giftCardsBasketSlice.actions.reset(undefined));
  }, [props.redirect_status, dispatch, orderFailedHandler]);

  return (
    <WithLoadingPage paymentStatus={data?.payment_status} orderLoaded={Boolean(order)}>
      <div className="h-full relative">
        {!isMobileApp && (
          <div className="sticky top-0 z-50 bg-brand-card">
            <PageHeader />
          </div>
        )}
        <div className="flex flex-col relative mx-auto items-center p-lg space-y-xl w-full max-w-[640px]">
          <TitleSection />
          <OrderDetailsSection {...(order && order)} />
          <InstructionsSection />
          <ButtonsSection {...(order && order)} />
          <FooterSection />
        </div>
      </div>
    </WithLoadingPage>
  );
};

const TitleSection = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:GiftCardsOrderDetailsScreen.sections.title' });

  return (
    <>
      <div className="rounded-full bg-green-300 p-3">
        <CheckCircle2 className="size-16 text-green-600" />
      </div>
      <div className="text-center space-y-md">
        <h3 className="text-md">{t('title')}</h3>
        <p className="text-muted-foreground">{t('description')}</p>
      </div>
    </>
  );
};

const OrderDetailsSection = (props: GiftCardOrder) => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:GiftCardsOrderDetailsScreen.sections.details' });
  const { formatCurrency } = useAppMethods();

  return (
    <div className="w-full border rounded-lg p-md space-y-md">
      <div className="flex justify-between">
        <p className="text-muted-foreground">{t('order_number')}</p>
        <p className="font-medium">#{props.order_number}</p>
      </div>
      <hr />
      <div className="flex justify-between [&_*]:!text-sm">
        <p className="text-muted-foreground">{t('total')}</p>
        <p className="font-medium">{formatCurrency(props.total)}</p>
      </div>
    </div>
  );
};

const InstructionsSection = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:GiftCardsOrderDetailsScreen.sections.instructions' });

  return (
    <div className="rounded-lg border border-brand-primary p-md bg-brand-primary/5 text-brand-primary">
      <h3 className="text-sm flex items-center space-x-xs">
        <Mail className="size-md" />
        <span>{t('title')}</span>
      </h3>
      <p className="px-md text-sm">{t('description')}</p>
    </div>
  );
};

const FooterSection = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:GiftCardsOrderDetailsScreen.sections.footer' });
  const company = useSelector(selectCompany);

  return (
    <div className="rounded-lg p-md w-full space-y-md bg-gray/20">
      <p className="text-sm text-muted-foreground">{t('help', { email: company?.service_provider?.sender_email })}</p>
      <p className="text-sm text-muted-foreground">{t('questions', { email: company?.email })}</p>
    </div>
  );
};

const getFileName = (index: number, lang?: string): string => {
  if (lang === 'en') {
    return index === 0 ? 'gift-card.pdf' : `gift-card-${index}.pdf`;
  }

  return index === 0 ? 'Dein-Restaurantgutschein.pdf' : `Dein-Restaurantgutschein-${index}.pdf`;
};

const ButtonsSection: FC<GiftCardOrder> = (order) => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:GiftCardsOrderDetailsScreen.buttons' });
  const { navigate } = useRouter();
  const { isDesktop } = useScreenType();
  const goHome = () =>
    navigate({
      web: {
        to: SCREENS.HOME_SCREEN,
      },
      mobile: {
        path: Routes.Main,
      },
    });

  const onDownloadClick = async () => {
    if (!order?.gift_cards) {
      return;
    }

    return Promise.all(
      order.gift_cards.map((url, index) =>
        fetch(url, { cache: 'no-store' })
          .then((res) => res.blob())
          .then((blob) => saveAs(blob, getFileName(index))),
      ),
    );
  };

  const hasDownloadButton = isDesktop && !!order?.gift_cards?.length;

  return (
    <div className="w-full space-y-md">
      {hasDownloadButton && (
        <CustomButton
          title={t('download_pdfs')}
          buttonType={ButtonTypes.forth}
          customIcon={<Download className="size-md mr-md text-brand-primary" />}
          onClick={onDownloadClick}
        />
      )}
      <CustomButton title={t('go_home')} onClick={goHome} />
    </div>
  );
};

interface WithLoadingPageProps extends PropsWithChildren {
  paymentStatus?: ORDER_PAYMENT_STATUSES;
  orderLoaded: boolean;
}

const WithLoadingPage: FC<WithLoadingPageProps> = ({ paymentStatus, children, orderLoaded }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });

  if (orderLoaded) {
    return children;
  }

  return (
    <LoadingPage description={paymentStatus === ORDER_PAYMENT_STATUSES.PENDING && t('loading.processing_payment')} />
  );
};

export default GiftCardsOrderDetailsScreen;
